/**
 * Custom Deck Creation — Single-device mode.
 * Each player privately enters their cards with phone-passing handoff.
 */

import { getState, updateSettings, selectDeck as selectDeckState, startGame as startGameState } from '../game-state';
import { Card, CardPoints } from '../../shared/types';
import { navigate } from '../router';
import { t } from '../i18n';

interface CustomDeckState {
  cardsPerPlayer: number;
  playerQueue: { id: string; name: string }[];
  currentPlayerIndex: number;
  currentPlayerCards: Card[];
  allCards: Card[];
  phase: 'handoff' | 'entry' | 'saved';
}

let deckState: CustomDeckState | null = null;

export function startCustomDeckCreation(): void {
  const state = getState();
  const players = state.players;
  const cpp = state.settings.cardsPerPlayer;

  deckState = {
    cardsPerPlayer: cpp,
    playerQueue: players.map((p) => ({ id: p.id, name: p.displayName })),
    currentPlayerIndex: 0,
    currentPlayerCards: [],
    allCards: [],
    phase: 'handoff',
  };

  renderCustomDeck();
}

export function renderCustomDeck(): void {
  if (!deckState) return;

  switch (deckState.phase) {
    case 'handoff':
      renderHandoff();
      break;
    case 'entry':
      renderCardEntry();
      break;
    case 'saved':
      renderSaved();
      break;
  }
}

function renderHandoff(): void {
  const app = document.getElementById('app')!;
  const currentPlayer = deckState!.playerQueue[deckState!.currentPlayerIndex];
  const total = deckState!.playerQueue.length;
  const done = deckState!.currentPlayerIndex;

  app.innerHTML = `
    <div class="stack center">
      <h1 style="font-size: var(--font-size-xl);">${t('customDeckTitle')}</h1>
      <p class="text-muted">${t('cardsPerPlayerLabel', deckState!.cardsPerPlayer)}</p>

      <div class="surface-elevated" style="padding: var(--space-xl);">
        <p class="text-muted" style="margin-bottom: var(--space-sm);">${t('passPhoneToPlayer')}</p>
        <div style="font-size: var(--font-size-2xl); font-weight: var(--font-weight-bold); color: var(--color-accent);">
          ${currentPlayer.name}
        </div>
      </div>

      <button class="btn btn-primary" id="btn-ready" style="font-size: var(--font-size-lg); padding: var(--space-lg);">
        ${t('imReady')}
      </button>

      <p class="text-muted" style="font-size: var(--font-size-sm);">
        ${t('progressLabel', done, total)}
      </p>
    </div>
  `;

  document.getElementById('btn-ready')?.addEventListener('click', () => {
    deckState!.phase = 'entry';
    deckState!.currentPlayerCards = [];
    renderCustomDeck();
  });
}

function renderCardEntry(): void {
  const app = document.getElementById('app')!;
  const currentPlayer = deckState!.playerQueue[deckState!.currentPlayerIndex];
  const cards = deckState!.currentPlayerCards;
  const needed = deckState!.cardsPerPlayer;
  const canSubmit = cards.length >= needed;

  app.innerHTML = `
    <div class="stack">
      <h1 style="font-size: var(--font-size-xl);">${t('playerCards', currentPlayer.name)}</h1>
      <p class="text-muted">${cards.length}/${needed} ${t('cards')}</p>

      ${!canSubmit ? `
        <!-- Card Entry Form -->
        <div class="surface" style="padding: var(--space-md);">
          <div class="stack-sm">
            <div>
              <label class="setting-label">${t('titleRequired')}</label>
              <input type="text" id="card-title" class="input" maxlength="80"
                placeholder="${t('titlePlaceholder')}" autofocus />
            </div>
            <div>
              <label class="setting-label">${t('descriptionOptional')}</label>
              <input type="text" id="card-desc" class="input" maxlength="200"
                placeholder="${t('descPlaceholder')}" />
            </div>
            <div>
              <label class="setting-label">${t('points')}</label>
              <div class="toggle-group">
                <button class="toggle-btn" data-pts="1">1</button>
                <button class="toggle-btn active" data-pts="2">2</button>
                <button class="toggle-btn" data-pts="3">3</button>
                <button class="toggle-btn" data-pts="4">4</button>
              </div>
            </div>
          </div>
        </div>

        <button class="btn btn-primary" id="btn-add-card">${t('addCard')}</button>
        <p id="card-error" class="error-text" style="min-height: 1.2em;"></p>
      ` : ''}

      <!-- Cards Added List -->
      ${cards.length > 0 ? `
        <div class="surface" style="padding: var(--space-md);">
          <p class="setting-label" style="margin-bottom: var(--space-xs);">${t('cardsAdded')}</p>
          <div class="stack-sm">
            ${cards.map((c, i) => `
              <div class="row" style="justify-content: space-between; font-size: var(--font-size-sm);">
                <span>${i + 1}. "${c.title}"</span>
                <span class="text-muted">${c.points}${t('pt')}</span>
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}

      ${canSubmit ? `
        <button class="btn btn-primary" id="btn-submit-cards" style="font-size: var(--font-size-lg);">
          ${t('doneSubmitAll')}
        </button>
      ` : ''}
    </div>
  `;

  // Points toggle
  let selectedPoints: CardPoints = 2;
  document.querySelectorAll('[data-pts]').forEach((btn) => {
    btn.addEventListener('click', () => {
      selectedPoints = parseInt((btn as HTMLElement).dataset.pts!) as CardPoints;
      document.querySelectorAll('[data-pts]').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
    });
  });

  // Add card
  document.getElementById('btn-add-card')?.addEventListener('click', () => {
    const titleEl = document.getElementById('card-title') as HTMLInputElement;
    const descEl = document.getElementById('card-desc') as HTMLInputElement;
    const errorEl = document.getElementById('card-error')!;

    const title = titleEl.value.trim();
    if (!title) {
      errorEl.textContent = t('titleRequiredError');
      return;
    }
    if (title.length > 80) {
      errorEl.textContent = t('titleTooLong');
      return;
    }

    // Check for duplicate titles in this player's cards
    if (cards.some((c) => c.title.toLowerCase() === title.toLowerCase())) {
      errorEl.textContent = t('cardAlreadyAdded');
      return;
    }

    const card: Card = {
      id: `custom-${currentPlayer.id}-${cards.length}`,
      category: 'Custom',
      title,
      description: descEl.value.trim() || title,
      points: selectedPoints,
    };

    deckState!.currentPlayerCards.push(card);
    renderCustomDeck();
  });

  // Submit all cards
  document.getElementById('btn-submit-cards')?.addEventListener('click', () => {
    deckState!.allCards.push(...deckState!.currentPlayerCards);
    deckState!.phase = 'saved';
    renderCustomDeck();
  });
}

function renderSaved(): void {
  const app = document.getElementById('app')!;
  const nextIndex = deckState!.currentPlayerIndex + 1;
  const isLast = nextIndex >= deckState!.playerQueue.length;

  app.innerHTML = `
    <div class="stack center">
      <h1 style="color: var(--color-success);">${t('cardsSaved')}</h1>

      ${isLast ? `
        <p class="text-secondary">${t('allPlayersSubmitted')}</p>
        <p class="text-muted" style="font-size: var(--font-size-sm);">
          ${t('totalCardsReady', deckState!.allCards.length)}
        </p>
        <button class="btn btn-primary" id="btn-start-game" style="font-size: var(--font-size-lg);">
          ${t('startPlaying')}
        </button>
      ` : `
        <p class="text-secondary">${t('passToNextPlayer')}</p>
        <p class="text-muted" style="font-size: var(--font-size-sm);">${t('doNotScrollUp')}</p>
        <button class="btn btn-primary" id="btn-continue">${t('continue')}</button>
      `}
    </div>
  `;

  document.getElementById('btn-continue')?.addEventListener('click', () => {
    deckState!.currentPlayerIndex++;
    deckState!.phase = 'handoff';
    renderCustomDeck();
  });

  document.getElementById('btn-start-game')?.addEventListener('click', () => {
    finishCustomDeck();
  });
}

function finishCustomDeck(): void {
  // Build a Deck object from all cards
  const customDeck = {
    id: 'custom',
    name: t('customDeck'),
    description: `${t('customDeckDesc')} (${deckState!.allCards.length} ${t('cards')})`,
    cards: deckState!.allCards,
  };

  selectDeckState(customDeck);

  // Now start the game
  const error = startGameState();
  if (error) {
    alert(error);
    return;
  }

  deckState = null;
  navigate('round-interstitial');
}

export function getCustomDeckCards(): Card[] {
  return deckState?.allCards || [];
}
